import {
  all,
  delay,
  put,
  takeEvery,
  takeLatest
} from "@redux-saga/core/effects";
import {
  createNewDevice,
  createNewPicture,
  editPictureAction,
  getEmployeeCaptures,
  getLastCaptures,
  getPendingEmployeeCaptures,
  getPictureStatusData,
  reloadPictureUpload,
  updateCapturedPictureApproval
} from "../../Services/database";
import store from "../store";
import { getFileUrl } from "../../Services/storage";
import { isValidObject } from "../../Services/validators";
import { setErrorStatus, setSuccessStatus } from "../status/action";
import {
  getReferencePictureAsset,
  getSelectedEmployeeCapture,
  getSelectedEmployeeCaptureAssets,
  getSelectedEmployeePendingCaptures
} from "./action";

export const actionTypes = {
  CREATE_PICTURE: "CREATE_PICTURE",
  EDIT_PICTURE: "EDIT_PICTURE",
  CREATE_DEVICE: "CREATE_DEVICE",
  PUT_REFERENCE_PICTURE: "PUT_REFERENCE_PICTURE",
  GET_REFERENCE_PICTURE_ASSETS: "GET_REFERENCE_PICTURE_ASSETS",
  GET_EMPLOYEE_CAPTURED_PICTURE: "GET_EMPLOYEE_CAPTURED_PICTURE",
  GET_PICTURE_STATUS: "GET_PICTURE_STATUS",
  PUT_DEVICES: "PUT_DEVICES",
  RETRY_PICTURE_UPLOAD_TO_DEVICES: "RETRY_PICTURE_UPLOAD_TO_DEVICES",
  GET_EMPLOYEE_CAPTURED_PICTURE_ASSETS: "GET_EMPLOYEE_CAPTURED_PICTURE_ASSETS",
  APPROVE_CAPTURE_PICTURE: "APPROVE_CAPTURE_PICTURE",
  GET_PENDING_CAPTURES: "GET_PENDING_CAPTURES",
  DELETE_REFERENCE_PICTURE: "DELETE_REFERENCE_PICTURE",
  SET_LIVE_FEED_AUTO_SWITCH_TIME: "SET_LIVE_FEED_AUTO_SWITCH_TIME"
};

function* createPictureWorker(action) {
  try {
    setSurveillanceLoading(true);
    const data = action.payload.data;
    yield createNewPicture(data, action.payload.asset);
    yield setSuccessStatus("Picture updated successfully");
    if (data.employee.id) {
      getReferencePictureAsset(data.employee.id);
    }

    setSurveillanceLoading(false);
  } catch (error) {
    setSurveillanceLoading(false);
    console.error("createPictureWorker", error);
  }
}

function* editPictureWorker(action) {
  try {
    setSurveillanceLoading(true);
    yield createNewPicture(action.payload.data, action.payload.asset);
    yield editPictureAction(action.payload.documentId, "update");
    const employeeId = store.getState().table.workforce.selectedEmployee;
    if (employeeId) {
      getReferencePictureAsset(employeeId);
    }

    setSurveillanceLoading(false);
  } catch (error) {
    setSurveillanceLoading(false);
    console.error("update picture worker", error);
  }
}

function* createDeviceWorker(action) {
  try {
    setSurveillanceLoading(true);

    let deviceData = action.payload.data;

    const selectedLocation = Object.values(
      store.getState().locations.data
    )?.find((data) => data.PCTNo === deviceData.locationId);

    delete deviceData.locationId;

    yield createNewDevice({
      ...deviceData,
      location: {
        id: selectedLocation.documentId,
        PCTNo: selectedLocation.PCTNo,
        address: selectedLocation.location,
        ward: selectedLocation.ward,
        zone: selectedLocation.zone
      },
      updatedAt: +new Date()
    });

    setSurveillanceLoading(false);
  } catch (error) {
    setSurveillanceLoading(false);
    console.error("createDeviceWorker", error);
  }
}

function* putReferencePictureWorker(action) {
  try {
    setSurveillanceLoading(true);
    yield put({
      type: "SET_REFERENCE_PICTURE",
      payload: {
        data: action.payload.data
      }
    });

    if (store.getState().table?.workforce?.selectedEmployee) {
      getReferencePictureAsset(
        store.getState().table?.workforce?.selectedEmployee
      );
    }
    setSurveillanceLoading(false);
  } catch (error) {
    setSurveillanceLoading(false);
    console.error("setReferencePictureWorker", error);
  }
}

function* getReferencePictureAssetsWorker(action) {
  try {
    const referenceData = store.getState().surveillance.reference;
    const employeeId =
      action.payload.documentId ||
      store.getState().table.workforce.selectedEmployee;

    if (!employeeId) {
      return;
    }

    if (isValidObject(referenceData)) {
      const referenceInfo = Object.values(referenceData).find(
        (data) => data.employee?.id === employeeId
      );

      if (referenceInfo?.asset) {
        const fileSrc = yield getFileUrl(referenceInfo.asset);
        yield put({
          type: "SET_REFERENCE_PICTURE_ASSET",
          payload: {
            data: {
              [referenceInfo?.documentId]: {
                ...referenceInfo,
                fileSrc: fileSrc?.url
              }
            }
          }
        });
      }
    }
  } catch (error) {
    console.error("setReferencePictureWorker", error);
  }
}

function* getEmployeeCapturedPictureWorker(action) {
  try {
    setSurveillanceLoading(true);

    const captureData = yield getEmployeeCaptures(
      action.payload.documentId,
      store.getState().table.workforce.captureFilter.dateRange
    );

    yield put({
      type: "SET_CAPTURED_PICTURE",
      payload: {
        data: { [action.payload.documentId]: captureData }
      }
    });

    getSelectedEmployeeCaptureAssets(1, action.payload.documentId);

    setSurveillanceLoading(false);
  } catch (error) {
    setSurveillanceLoading(false);
    console.error("setCapturedPictureWorker", error);
  }
}

function* getEmployeeCapturedPictureAssetsWorker(action) {
  try {
    const capture = Object.values(
      store.getState().surveillance?.captures?.[action.payload.employeeId]
    )?.slice(
      action.payload.currentPage * 50 - 50,
      action.payload.currentPage * 50
    );

    for (const captureData of capture) {
      if (typeof captureData.fileSrc === "string") {
        continue;
      }
      const fileSrc = yield getFileUrl(captureData.asset);
      if (
        store.getState().surveillance?.captures?.[captureData.employee.id]?.[
          captureData?.documentId
        ]
      ) {
        yield put({
          type: "SET_CAPTURED_PICTURE_ASSET",
          payload: {
            data: {
              [captureData.documentId]: { ...captureData, fileSrc: fileSrc.url }
            },
            employeeId: captureData.employee.id
          }
        });
      }
    }
  } catch (error) {
    console.error("setCapturedPictureAssetsWorker", error);
  }
}

function* getPictureStatusWorker(action) {
  try {
    setSurveillanceLoading(true);

    const surveillanceReference = store.getState()?.surveillance?.reference;
    if (!isValidObject(surveillanceReference)) {
      setSurveillanceLoading(false);
      return;
    }

    const pictureData = Object.values(surveillanceReference).find(
      (data) => data.employee?.id === action.payload.employeeId
    );
    if (isValidObject(pictureData)) {
      const statusData = yield getPictureStatusData(pictureData.documentId);

      if (isValidObject(statusData)) {
        yield put({
          type: "SET_PICTURE_STATUS",
          payload: {
            data: {
              [action.payload.employeeId]: statusData
            }
          }
        });
      }
    }

    setSurveillanceLoading(false);
  } catch (error) {
    setSurveillanceLoading(false);
    console.error("getPictureStatusWorker", error);
  }
}

function* putDevicesWorker(action) {
  try {
    yield put({
      type: "SET_DEVICES",
      payload: {
        data: action.payload.data
      }
    });
  } catch (error) {
    setSurveillanceLoading(false);
    console.error("putDevicesWorker", error);
  }
}

function* retryPictureUploadToDevicesWorker(action) {
  try {
    yield setSurveillanceRetryLoading(true);

    yield reloadPictureUpload(action.payload.documentId);

    setSuccessStatus("Retrying successfully");

    yield setSurveillanceRetryLoading(false);
  } catch (error) {
    yield setSurveillanceRetryLoading(false);
    yield setErrorStatus(error);
    console.error("retryPictureUploadToDevicesWorker", error);
  }
}

function* approveCapturePictureWorker(action) {
  try {
    yield setSurveillancePictureApprovalLoading(true);
    if (action.payload.approved) {
      const profileData = store.getState().profile.data;
      yield updateCapturedPictureApproval(
        action.payload.capturedData.documentId,
        {
          approval: {
            status: "APPROVED",
            updatedAt: +new Date(),
            approvedBy: {
              id: profileData.employeeId,
              email: profileData.email,
              name: profileData.name
            }
          },
          ...(action.payload.reference
            ? { reference: action.payload.reference }
            : {}),
          updateReference: action.payload.updateReference,
          success: true
        }
      );

      if (action.payload.updateReference) {
        let referenceData = null;
        let attempts = 0;

        while (
          (!isValidObject(referenceData) || referenceData?.vmSync !== true) &&
          attempts < 10
        ) {
          yield delay(2000);
          referenceData = Object.values(
            store.getState().surveillance.reference
          ).find(
            (data) =>
              data.employee.id === action.payload.capturedData.employee.id
          );
          attempts++;
        }

        if (referenceData) {
          yield updateCapturedPictureApproval(
            action.payload.capturedData.documentId,
            {
              reference: {
                pictureId: referenceData.documentId,
                cosineDistance: 1
              }
            }
          );
        }
      }

      setSuccessStatus("Manual approval granted!");
    } else {
      yield updateCapturedPictureApproval(
        action.payload.capturedData.documentId,
        {
          approval: {
            status: "DENIED",
            updatedAt: +new Date()
          }
        }
      );
      setSuccessStatus("Manual approval rejected!");
    }

    const tableData = store.getState().table;

    getSelectedEmployeeCapture(tableData.workforce.selectedEmployee);
    getSelectedEmployeePendingCaptures(tableData.workforce.selectedEmployee);

    yield setSurveillancePictureApprovalLoading(false);
  } catch (error) {
    yield setSurveillancePictureApprovalLoading(false);
    yield setErrorStatus(error);
    console.error("approveCapturePictureWorker", error);
  }
}

function* getPendingCapturesWorker(action) {
  try {
    yield setSurveillanceLoading(true);

    const pendingCaptures = yield getPendingEmployeeCaptures(
      action.payload.employeeId
    );

    yield put({
      type: "SET_PENDING_CAPTURED_PICTURE",
      payload: {
        data: { [action.payload.employeeId]: pendingCaptures }
      }
    });

    yield setSurveillanceLoading(false);

    if (!isValidObject(pendingCaptures)) {
      yield put({
        type: "SET_FORCE_REFERENCE_PICTURE_UPDATE",
        payload: {
          update: false
        }
      });
      return;
    }

    const lastCaptures = yield getLastCaptures(action.payload.employeeId, 5);
    const isManualApproved = lastCaptures?.every(
      (data) =>
        data.reference?.[0]?.euclideanDistance === 0 ||
        data.reference?.[0]?.cosineDistance === 1
    );

    yield put({
      type: "SET_FORCE_REFERENCE_PICTURE_UPDATE",
      payload: {
        update: lastCaptures?.length === 5 && isManualApproved ? true : false
      }
    });

    for (const captureData of Object.values(pendingCaptures)) {
      if (typeof captureData.fileSrc === "string") {
        continue;
      }
      const fileSrc = yield getFileUrl(captureData.asset);
      if (
        store.getState().surveillance?.pendingCaptures?.[
          captureData.employee.id
        ]?.[captureData?.documentId]
      ) {
        yield put({
          type: "SET_PENDING_CAPTURED_PICTURE_ASSET",
          payload: {
            data: {
              [captureData.documentId]: { ...captureData, fileSrc: fileSrc.url }
            },
            employeeId: captureData.employee.id
          }
        });
      }
    }
  } catch (error) {
    yield setSurveillanceLoading(false);
    yield setErrorStatus(error);
    console.error("approveCapturePictureWorker", error);
  }
}

function* deleteReferencePictureWorker(action) {
  try {
    yield setSurveillanceLoading(true);

    yield editPictureAction(action.payload.documentId, "delete");

    setSuccessStatus("Reference photo deleted successfully");

    yield setSurveillanceLoading(false);
  } catch (error) {
    yield setSurveillanceLoading(false);
    yield setErrorStatus(error);
    console.error("deleteReferencePictureWorker", error);
  }
}

function* setLiveFeedAutoSwitchTimeWorker(action) {
  try {
    yield setSurveillanceLoading(true);

    yield put({
      type: "PUT_LIVE_FEED_AUTO_SWITCH_TIME",
      payload: {
        time: action.payload.time
      }
    });

    yield setSurveillanceLoading(false);
  } catch (error) {
    yield setSurveillanceLoading(false);
    yield setErrorStatus(error);
    console.error("setLiveFeedAutoSwitchTimeWorker", error);
  }
}

export default function* surveillanceWatcher() {
  yield all([
    takeEvery("CREATE_PICTURE", createPictureWorker),
    takeEvery("EDIT_PICTURE", editPictureWorker),
    takeEvery("CREATE_DEVICE", createDeviceWorker),
    takeEvery("PUT_REFERENCE_PICTURE", putReferencePictureWorker),
    takeEvery("GET_REFERENCE_PICTURE_ASSETS", getReferencePictureAssetsWorker),
    takeEvery(
      "GET_EMPLOYEE_CAPTURED_PICTURE",
      getEmployeeCapturedPictureWorker
    ),
    takeLatest(
      "GET_EMPLOYEE_CAPTURED_PICTURE_ASSETS",
      getEmployeeCapturedPictureAssetsWorker
    ),
    takeEvery("GET_PICTURE_STATUS", getPictureStatusWorker),
    takeEvery("PUT_DEVICES", putDevicesWorker),
    takeEvery(
      "RETRY_PICTURE_UPLOAD_TO_DEVICES",
      retryPictureUploadToDevicesWorker
    ),
    takeEvery("APPROVE_CAPTURE_PICTURE", approveCapturePictureWorker),
    takeEvery("GET_PENDING_CAPTURES", getPendingCapturesWorker),
    takeEvery("DELETE_REFERENCE_PICTURE", deleteReferencePictureWorker),
    takeEvery("SET_LIVE_FEED_AUTO_SWITCH_TIME", setLiveFeedAutoSwitchTimeWorker)
  ]);
}

function* setSurveillanceLoading(bool) {
  yield put({
    type: "SET_SURVEILLANCE_LOADING",
    payload: {
      loading: bool
    }
  });
}

function* setSurveillancePictureApprovalLoading(bool) {
  yield put({
    type: "SET_SURVEILLANCE_PICTURE_APPROVAL_LOADING",
    payload: {
      loading: bool
    }
  });
}

function* setSurveillanceRetryLoading(bool) {
  yield put({
    type: "SET_SURVEILLANCE_RETRY_LOADING",
    payload: {
      loading: bool
    }
  });
}
